import {Injectable} from '@angular/core';
import {
  BillingAddress,
  BillingCycleType,
  CalculateActivePayload,
  CalculateOnTrialActivePayload,
  ICalculateAutomatedPdfPlan,
  ICalculateEstimateItem,
  ICalculateEstimateItemSite,
  ICalculateRequest,
  InvoiceTotal,
  PaymentStep,
  Plan,
  ProductType,
  StripeProduct,
  TierPlan,
} from '../types/payment.type';
import {MultiSite} from '../types/site.type';
import {finalize, skip, switchMap, tap} from 'rxjs/operators';
import {BehaviorSubject, combineLatest, forkJoin, lastValueFrom, Observable, of, Subject} from 'rxjs';
import {StripeHelperService} from './stripe-helper.service';
import {ICreateInstance, RemediationSubmissionState} from '../types/pdf.type';
import {SettingsApiService} from './api/settings-api.service';
import {EventEmitterService} from './event-emitter.service';
import {MCActionsService} from './mc-base/mc-actions-service';
import {ToastrMessagesProvider} from './toastr-messages.service';
import {billingAddressMap} from '../common/utils/billing-address-map';
import {UserInfoQuery} from '../common/elf-store/user-info/user-info.query';
import {PaymentDataQuery} from '../common/elf-store/payment-data/payment-data.query';
import {PaymentDataManager} from '../common/elf-store/payment-data/payment-data.manager';
import {SubscriptionService} from './subscriptions.service';
import {UserPermissionsService} from './user-permissions.service';
import {UserPermissions} from '../common/elf-store/user-permissions/user-permissions.store';
import {
  CalculateCartBodyDto,
  CalculateCartTrialPayNowBodyDto,
  PaymentMethodDetailsWithAddressDto,
  SiteSearchEntryDto,
  SubscriptionBillingCycleType,
  CreateUserSharedPaymentLinkDto,
} from '../../userway/sdk/UserWayApiClient';
import {ErrorMessage} from '../types/toastr-notifications.type';

@Injectable({
  providedIn: 'root',
})
export class PaymentDataService extends MCActionsService {
  selectedProduct: ProductType;
  lastSelectedProduct: ProductType;
  isSelectedProductAddon: boolean;
  productId: string;
  productPrice: StripeProduct;
  productSubscriptionId: number;
  pricingTier: TierPlan;
  liveTranslationsPlan: TierPlan;

  sites: MultiSite[];
  removedSites: ICalculateEstimateItemSite[] = [];
  customerId: string;
  userServiceId: number;
  uwSubscriptionId: number;
  subscriptionId: string;

  billingCycle: BillingCycleType;
  paymentMethod: PaymentMethodDetailsWithAddressDto;
  multiPaymentMethods: PaymentMethodDetailsWithAddressDto[];
  paymentIntentId: string;
  oneTimePaymentData: ICreateInstance;
  invoiceTotal: InvoiceTotal;
  discountPercents: number = null;

  isTrialActive: boolean;
  isOnTrialPeriod: boolean = false;
  isSubscriptionOnTrialPeriod: boolean = false;
  isSwitchingToAnnual: boolean;
  upgradeSubscriptionPlan: boolean = false;
  cancelRunningSubscription: boolean | null = null;

  estimatedProductData: ICalculateEstimateItem[];
  calculating: boolean = false;
  orderId: number;
  flowId: string;
  pdfQuantity: number;
  pdfSubscriptionId: string;

  isUpdatePlans: boolean;
  updatePlans: boolean = false;
  loadingSetDataPayment: boolean = false;
  paymentMethodLoading: boolean = false;
  shouldHidePaymentOptions: boolean = false;
  shouldHideCoupon: boolean = false;

  couponApplied: boolean;
  coupon = new BehaviorSubject<string | null>(null);
  couponError = new BehaviorSubject<boolean>(false);

  invoiceDto = new BehaviorSubject(null);
  calculatedResponseSubscriber = new Subject<any>();
  calculateError = new BehaviorSubject(false);
  calculateDone$ = new Subject<void>();
  successfullyPaid$ = new BehaviorSubject<boolean>(false);
  unrecognizedLocationError$ = new Subject<boolean>();

  constructor(
    private toastr: ToastrMessagesProvider,
    private stripeService: StripeHelperService,
    private settingsApiService: SettingsApiService,
    private eventEmitterService: EventEmitterService,
    private paymentQuery: PaymentDataQuery,
    public subscriptionService: SubscriptionService,
    private paymentManager: PaymentDataManager,
    private userPermissionsService: UserPermissionsService,
    userInfoQuery: UserInfoQuery,
  ) {
    super(userInfoQuery);
    this.initializeService();
    this.subscribeToPaymentData();
  }

  private initializeService(): void {
    this.initialized$.next(true);
  }

  private subscribeToPaymentData(): void {
    combineLatest({
      isTrialActive: this.paymentQuery.isTrialActive$,
      selectedProduct: this.paymentQuery.selectedProduct$,
      sites: this.paymentQuery.sites$,
      isSelectedProductAddon: this.paymentQuery.isSelectedProductAddon$,
      isSwitchingToAnnual: this.paymentQuery.isSwitchingToAnnual$,
      customerId: this.paymentQuery.customerId$,
      estimatedProductData: this.paymentQuery.estimatedProductData$,
      isOnTrialPeriod: this.paymentQuery.isOnTrialPeriod$,
      billingCycle: this.paymentQuery.billingCycle$,
      isSubscriptionOnTrialPeriod: this.paymentQuery.isSubscriptionOnTrialPeriod$,
      calculating: this.paymentQuery.isLoading$,
    }).subscribe(this.updatePaymentState.bind(this));
  }

  private updatePaymentState({
    isTrialActive,
    selectedProduct,
    calculating,
    sites,
    isSelectedProductAddon,
    isSwitchingToAnnual,
    customerId,
    estimatedProductData,
    isOnTrialPeriod,
    billingCycle,
    isSubscriptionOnTrialPeriod,
  }): void {
    this.isTrialActive = isTrialActive;
    this.selectedProduct = selectedProduct;
    this.sites = sites;
    this.isSelectedProductAddon = isSelectedProductAddon;
    this.isSwitchingToAnnual = isSwitchingToAnnual;
    this.customerId = customerId;
    this.calculating = calculating;
    this.estimatedProductData = estimatedProductData;
    this.isOnTrialPeriod = isOnTrialPeriod;
    this.billingCycle = SubscriptionBillingCycleType[billingCycle];
    this.isSubscriptionOnTrialPeriod = isSubscriptionOnTrialPeriod;
  }

  get isNoCardTrial() {
    return this.userInfo?.trialInfo?.isTrialNoCard;
  }

  mcOnLogout() {
    this.resetCustomerAndPaymentData();
  }

  mcOnLogin() {
    const permissionSub = this.userPermissionsService
      .checkUserPermissions(UserPermissions.manageBilling.billingOperations)
      .pipe(skip(1))
      .subscribe(isUserHavePermissions => {
        if (isUserHavePermissions) {
          void this.getCustomer();
          void this.getPaymentMethod();
        }
        permissionSub?.unsubscribe();
      });
  }

  getCustomer() {
    return lastValueFrom(this.stripeService.getCustomer());
  }

  setPaymentData(
    selectedProduct: ProductType,
    sites: MultiSite[],
    billingCycle: BillingCycleType,
    isUpdatePlans: boolean = false,
    orderId?: number,
    shouldCalculate: boolean = true,
  ): Promise<StripeProduct[]> {
    this.loadingSetDataPayment = true;
    this.paymentManager.setSelectedProduct(selectedProduct);
    this.paymentManager.setSites(sites);
    this.lastSelectedProduct = selectedProduct;
    this.paymentManager.setBillingCycle(billingCycle);

    this.isUpdatePlans = isUpdatePlans;
    this.orderId = orderId;
    return this.getProducts(shouldCalculate);
  }

  async getProducts(shouldCalculate: boolean = true, productType: ProductType | null = null): Promise<StripeProduct[]> {
    const products = await lastValueFrom(this.stripeService.getProducts());
    await this.setupProduct(products, productType);
    await this.handleProductCalculation(shouldCalculate);
    return products;
  }

  private async setupProduct(products: StripeProduct[], productType: ProductType | null): Promise<void> {
    if (productType) {
      const foundProduct = products.find((product: {name: ProductType}) => product.name === productType);
      this.selectedProduct = foundProduct.name;
      this.productId = foundProduct.id;
    }

    if (products && this.selectedProduct) {
      this.productPrice = products.find(product => product.name === this.selectedProduct);
      this.productId = this.productPrice?.id;
    }
  }

  private async handleProductCalculation(shouldCalculate: boolean): Promise<void> {
    if (shouldCalculate && this.selectedProduct) {
      await this.calculate().finally(() => {
        this.finalizeProductSetup();
      });
    } else {
      this.finalizeProductSetup();
    }
  }

  private finalizeProductSetup(): void {
    this.loadingSetDataPayment = false;
    this.eventEmitterService.loadedPaymentMethodForSubscription.next(false);
  }

  async calculatePdfSubscription(billingAddress?: BillingAddress) {
    if (!this.customerId) {
      await this.getCustomer();
    }

    if (!this.paymentMethod) {
      await this.getPaymentMethod();
    }
    const commonBillingData = {
      ...(billingAddress && {address: billingAddress}),
      ...(!billingAddress && {address: billingAddressMap(this.paymentMethod?.address)}),
    };
    let calculateRequest: Observable<any>;
    const requestData: ICalculateRequest = {
      customerId: this.customerId,
      subscriptionId: this.pdfSubscriptionId,
      billingCycle: this.billingCycle,
      items: [{productId: this.productId, sitesIds: this.sites?.map(site => site.id)}],
      ...commonBillingData,
    };

    const [item] = requestData.items;
    item.settings = {
      flowId: this.flowId,
      pricing_tier: this.pricingTier,
      quantity: this.pdfQuantity,
    };
    calculateRequest = this.stripeService.calculatePdfSubscription(requestData);
    return new Promise<void>((resolve, reject) => {
      calculateRequest.subscribe(
        (response: any) => {
          if (!response?.invoiceDto) {
            this.toastr.warning('Unable to prepare the invoice. Please contact the support team.');
            return;
          }
          this.invoiceTotal = response.invoiceDto;
          this.discountPercents = response.invoiceDto.discountPercentage;
          this.invoiceDto.next(response.invoiceDto);
          resolve();
        },
        err => {
          this.showErrorMessage(err);
          reject(err);
        },
      );
    });
  }

  async calculate(
    billingAddress?: BillingAddress,
    isUpdatingForm: boolean = false,
    state?: RemediationSubmissionState,
  ): Promise<void> {
    this.unrecognizedLocationError$.next(false);
    this.paymentManager.setLoading(true);

    await this.fetchInitialData(isUpdatingForm);
    const commonBillingData = this.prepareCommonBillingData(billingAddress);
    const isCompletePaymentState = Boolean(billingAddress) || Boolean(this.coupon.value);

    if (!this.isOnTrialPeriod && !this.productId) {
      this.calculating = false;
      return;
    }

    const calculateRequest = this.isOnTrialPeriod
      ? this.prepareTrialPeriodRequest(commonBillingData)
      : this.prepareStandardRequest(commonBillingData, this.isNoCardTrial);

    try {
      const response = await calculateRequest.pipe(finalize(() => this.finalizeCalculation())).toPromise();

      this.processResponse(response, isCompletePaymentState, state);
    } catch (err) {
      this.handleError(err);
    }
  }

  private async fetchInitialData(isUpdatingForm: boolean): Promise<void> {
    if (!this.customerId) await this.getCustomer();
    if (!this.paymentMethod && !isUpdatingForm) await this.getPaymentMethod();
  }

  private prepareCommonBillingData(billingAddress?: BillingAddress): any {
    return {
      ...(billingAddress
        ? {address: billingAddress}
        : this.paymentMethod
          ? {address: billingAddressMap(this.paymentMethod.address)}
          : {}),
      ...(this.coupon.value ? {coupon: this.coupon.value} : {}),
    };
  }

  private prepareTrialPeriodRequest(commonBillingData: any): Observable<any> {
    const requestData: CalculateCartTrialPayNowBodyDto = {
      formOrigin: 'MANAGE_CONSOLE',
      customerId: this.customerId,
      subscriptionId: this.subscriptionId,
      ...commonBillingData,
    };
    return this.stripeService.calculateOnTrial(requestData);
  }

  private prepareStandardRequest(commonBillingData: any, forceCalculation: boolean): Observable<any> {
    const requestData: CalculateCartBodyDto = {
      customerId: this.customerId,
      billingCycle: this.billingCycle,
      items: this.prepareItems(),
      forceCalculation,
      ...commonBillingData,
    };
    return this.stripeService.calculate(requestData);
  }

  private prepareItems(): any[] {
    return [
      {
        productId: this.productId,
        sitesIds: this.sites?.map(site => site.id),
        settings: this.getItemSettings(),
      },
    ];
  }

  private getItemSettings() {
    let settings;
    if (this.liveTranslationsPlan) {
      settings = {
        pricing_tier: this.liveTranslationsPlan,
      };
    }
    if (this.orderId) {
      settings = {
        ...settings,
        pdfRemediationOrderId: this.orderId,
      };
    }
    if (this.flowId) {
      settings = {
        ...settings,
        flowId: this.flowId,
        pricing_tier: this.pricingTier,
        quantity: this.pdfQuantity,
      };
    }
    return settings;
  }

  private finalizeCalculation(): void {
    this.paymentManager.setLoading(false);
    this.loadingSetDataPayment = false;
  }

  private processResponse(
    response: CalculateActivePayload | CalculateOnTrialActivePayload,
    isCompletePaymentState: boolean,
    state?: RemediationSubmissionState,
  ): void {
    this.checkCouponStatus(response);
    if (!response?.invoiceDto) {
      this.toastr.warning('Unable to prepare the invoice. Please contact the support team.');
      return;
    }

    if (!this.isOnTrialPeriod && 'estimate' in response) {
      this.setEstimatedProductData(response.estimate[this.selectedProduct]?.reverse());
      if (response.estimate[ProductType.PdfAutoRemediation]) {
        this.setAutomatedPdfPlan(response.estimate[ProductType.PdfAutoRemediation]);
      }
    }

    this.invoiceTotal = response.invoiceDto;
    this.discountPercents = response.invoiceDto.discountPercentage;
    this.invoiceDto.next(response.invoiceDto);
    this.calculatedResponseSubscriber.next({
      isCompletePaymentState,
      isUpdatePlans: this.isUpdatePlans,
      state,
    });
    this.calculateError.next(false);
    this.calculateDone$.next();

    if (this.isOnTrialPeriod) {
      this.eventEmitterService.openPaymentSiteDetails.next(PaymentStep.PAYMENT_FORM);
    }
  }

  private handleError(err: any): void {
    this.showErrorMessage(err);
    this.calculateError.next(true);
    this.eventEmitterService.loadedPaymentMethodForSubscription.next(false);
  }

  showErrorMessage(err: any) {
    const errorMassage = 'Unable to preview invoice. Unrecognized location';
    const error = err.response?.data?.error;
    if (error.includes(errorMassage)) {
      this.unrecognizedLocationError$.next(true);
      this.toastr.warning(ErrorMessage.InvalidLocationData);
    }
    this.calculateError.next(true);
  }

  checkCouponStatus(response: CalculateActivePayload | CalculateOnTrialActivePayload): void {
    this.couponApplied = response?.couponApplied;
    const isIncorrectCoupon = this.coupon?.value && !this.couponApplied;

    this.couponError.next(isIncorrectCoupon);
  }

  changeBillingCycle(billingCycle: SubscriptionBillingCycleType, shouldCalculate: boolean = true): void {
    this.paymentManager.setBillingCycle(billingCycle);
    if (shouldCalculate) {
      this.calculate(null, null, RemediationSubmissionState.SELECT_SITES_AND_PLAN);
    }
  }

  getPaymentMethod() {
    return this.stripeService
      .getDefaultPaymentMethod()
      .toPromise()
      .then((response: PaymentMethodDetailsWithAddressDto) => {
        this.paymentMethod = response;
      });
  }
  // TODO: - maybe this part of the logic is better to put on the BE part
  async getPaymentMethodBySubscriptionId(id = this.uwSubscriptionId) {
    return lastValueFrom(
      forkJoin({
        paymentMethodBySubscriptionId: this.stripeService.getPaymentMethodBySubscriptionId(id),
        multiPaymentMethods: this.stripeService.getMultiPaymentMethods(),
      }).pipe(
        tap(({paymentMethodBySubscriptionId, multiPaymentMethods}) => {
          this.paymentMethodLoading = false;
          const isPaymentMethodActive = multiPaymentMethods.paymentMethods.some(method => {
            return this.isPaymentMethodEqual(method, paymentMethodBySubscriptionId);
          });
          if (isPaymentMethodActive) {
            this.paymentMethod = paymentMethodBySubscriptionId;
          } else {
            this.paymentMethod = multiPaymentMethods.paymentMethods.find(method => method.isDefault);
          }
        }),
      ),
    );
  }

  getMultiPaymentMethods() {
    return this.stripeService.getMultiPaymentMethods().subscribe({
      next: response => {
        this.multiPaymentMethods = response.paymentMethods;
      },
    });
  }

  checkSubscriptionOnTrialOrUpgrade() {
    if (this.isSwitchingToAnnual || this.isSubscriptionOnTrialPeriod || this.isTrialActive) {
      const subscriptionId =
        this.productSubscriptionId || this.subscriptionService.selectedSubscription.uwSubscriptionId;
      this.getPaymentMethodBySubscriptionId(subscriptionId);
    } else {
      this.paymentMethodLoading = false;
    }
  }

  private isPaymentMethodEqual(
    method: PaymentMethodDetailsWithAddressDto,
    selectedMethod: PaymentMethodDetailsWithAddressDto | null,
  ): boolean {
    if (!method || !selectedMethod) {
      return false;
    }

    if (
      method.providerPaymentMethodId &&
      selectedMethod.providerPaymentMethodId &&
      method.providerPaymentMethodId === selectedMethod.providerPaymentMethodId
    ) {
      return true;
    }
    return method.uwPaymentDataId === selectedMethod.uwPaymentDataId;
  }

  resetPaymentProperties(): void {
    this.paymentManager.setSelectedProduct(null);
    this.paymentManager.setSites([]);
    this.paymentManager.setBillingCycle(null);
    this.removedSites = [];
    this.userServiceId = null;
    this.paymentManager.setLoading(false);

    this.productId = null;
    this.liveTranslationsPlan = null;
    this.cancelRunningSubscription = null;
    this.coupon.next(null);
    this.couponError.next(false);
    this.couponApplied = false;

    this.setEstimatedProductData(null);
    this.invoiceTotal = null;
    this.invoiceDto.next(null);
    this.paymentManager.setIsSwitchToAnnual(false);
    this.paymentManager.setIsOnTrialPeriod(false);
    this.paymentMethod = null;
    this.multiPaymentMethods = null;
    this.updatePlans = false;
    this.paymentManager.setIsSubscriptionOnTrialPeriod(false);
    this.paymentManager.setIsTrialActive(false);
    this.pdfQuantity = null;
    this.pdfSubscriptionId = null;
    this.flowId = null;
    this.upgradeSubscriptionPlan = false;
    this.shouldHidePaymentOptions = false;
    this.shouldHideCoupon = false;
    this.pricingTier = null;
    this.productSubscriptionId = null;
  }

  resetSwitchToAnnualAndTrial(): void {
    this.setIsSubscriptionOnTrialPeriod(false);
    this.paymentManager.setIsSwitchToAnnual(false);
  }

  resetCustomerAndPaymentData(): void {
    this.paymentManager.setCustomerId(null);
    this.resetPaymentProperties();
  }

  async upgradeSubscriptionRemediation(proPlusPlan: Plan, subscriptionId: string) {
    await this.getPaymentMethodBySubscriptionId(this.uwSubscriptionId);

    if (!this.customerId) {
      await this.getCustomer();
    }

    const requestData = {
      formOrigin: 'MANAGE_CONSOLE',
      customerId: this.customerId,
      subscriptionId,
      address: this.paymentMethod.address,
    };

    this.stripeService.calculateUpgradeRemediation(requestData).subscribe({
      next: response => {
        if (!response?.invoiceDto) {
          this.toastr.warning('Unable to prepare the invoice. Please contact the support team.');
          return;
        }
        this.invoiceTotal = response.invoiceDto;
        this.invoiceDto.next(response.invoiceDto);
        this.upgradeSubscriptionPlan = true;
        this.shouldHidePaymentOptions = true;
        this.shouldHideCoupon = true;
        this.eventEmitterService.openPaymentSiteDetails.next(PaymentStep.PAYMENT_FORM);
      },
    });
  }

  private applyInvoiceResponse(response: {invoiceDto: InvoiceTotal}): void {
    this.invoiceTotal = response.invoiceDto;
    this.discountPercents = response.invoiceDto.discountPercentage;
    this.invoiceDto.next(response.invoiceDto);
  }

  private setSwitchingContextForSite(
    site: SiteSearchEntryDto,
    billingCycle: SubscriptionBillingCycleType,
    productType: ProductType,
  ): void {
    this.paymentManager.setSelectedProduct(productType);
    this.paymentManager.setSites([{id: site.id, url: site.url}]);
    this.paymentManager.setBillingCycle(billingCycle);
    this.paymentManager.setIsSwitchToAnnual(true);
  }

  private async ensureCustomerAndPaymentMethod(): Promise<void> {
    if (!this.customerId) {
      await this.getCustomer();
    }
    if (!this.paymentMethod) {
      await this.getPaymentMethod();
    }
  }

  /**
   * Prepares invoice and UI state in order to switch billing cycle either for a whole subscription
   * or for a specific product on a site. Opens the payment form with the calculated totals.
   */
  async switchSiteProductToAnotherBillingCycle({
    site,
    billingCycle,
    productType,
    paymentProviderSubscriptionId,
    uwSubscriptionId,
  }: {
    site?: SiteSearchEntryDto;
    billingCycle?: SubscriptionBillingCycleType;
    productType?: ProductType;
    paymentProviderSubscriptionId?: string;
    uwSubscriptionId?: number;
  }) {
    await this.ensureCustomerAndPaymentMethod();

    // Switching an entire existing subscription by provider subscription ID
    if (paymentProviderSubscriptionId) {
      return lastValueFrom(
        this.stripeService
          .calculateSwitchPlan({
            customerId: this.customerId,
            subscriptionId: paymentProviderSubscriptionId,
          })
          .pipe(
            tap(response => {
              this.applyInvoiceResponse(response);
              this.paymentManager.setIsSwitchToAnnual(true);
              this.userServiceId = uwSubscriptionId;
              this.eventEmitterService.openPaymentSiteDetails.next(PaymentStep.PAYMENT_FORM);
            }),
          ),
      );
    }

    // Switching a specific product of a site
    if (!site || !productType || !billingCycle) {
      console.warn('switchSiteProductToAnotherBillingCycle: missing site, productType or billingCycle');
      return null;
    }

    return lastValueFrom(
      this.settingsApiService.getProductPaidData(site.id, productType).pipe(
        switchMap(productData => {
          const subscriptionId = productData?.subscription?.payment_provider_sub_id;
          this.productId = productData?.subscription?.stripeSubscription?.plan?.product?.id;
          this.userServiceId = productData?.userService?.subscription_id;
          this.productSubscriptionId = productData?.subscription?.id;

          if (!subscriptionId) {
            return of(null);
          }

          return this.stripeService.calculateSwitchPlan({customerId: this.customerId, subscriptionId}).pipe(
            tap(response => {
              this.setSwitchingContextForSite(site, billingCycle, productType);
              this.applyInvoiceResponse(response);
              this.setEstimatedProductData([
                {
                  cost: response.invoiceDto?.totalUsd,
                  discount: response.invoiceDto?.discountUsd,
                  discountPercents: response.invoiceDto?.discountPercentage,
                  siteItem: {
                    id: site.id,
                    url: site.url,
                    host: site.host,
                    views: 0,
                  },
                },
              ]);
              this.eventEmitterService.openPaymentSiteDetails.next(PaymentStep.PAYMENT_FORM);
            }),
          );
        }),
      ),
    );
  }

  updateSites(sites: MultiSite[]) {
    this.paymentManager.setSites(sites);
  }

  selectProduct(product: ProductType) {
    this.paymentManager.setSelectedProduct(product);
  }

  setEstimatedProductData(data: ICalculateEstimateItem[]) {
    this.paymentManager.setEstimatedProductData(data);
  }

  setAutomatedPdfPlan(data: ICalculateAutomatedPdfPlan[]) {
    this.paymentManager.setAutomatedPdfPlan(data);
  }

  setIsSubscriptionOnTrialPeriod(value: boolean) {
    this.paymentManager.setIsSubscriptionOnTrialPeriod(value);
  }

  generatePaymentLink(): Observable<string> {
    const data: CreateUserSharedPaymentLinkDto = {
      siteIds: this.sites.map(site => site.id),
      productIds: [this.productId],
      billingCycle: SubscriptionBillingCycleType[this.billingCycle],
      userId: null,
    };
    return this.stripeService.generatePaymentLink(data);
  }

  async checkIfPaymentMethodInitialized() {
    if (!this.paymentMethod) {
      await this.calculate();
    }
  }
}
